import { BaseResponse } from 'domain/payload/response/BaseResponseDto';
import axiosInstance from '../config/AxiosConfig'
import { MonitorDashboardResp } from 'domain/payload/response/monitor/MonitorDashboardResp';
import { MonitorByAreaResponse } from 'domain/payload/response/monitor/MonitorByAreaResp';
import { DashboardConfigResp } from 'domain/payload/response/dashboard/DashboardConfigResp';
import { UserGrowthDashboardResp } from 'domain/payload/response/monitor/UserGrowthDashboardResp';
import { MonitorOverviewProject, MonitorOverviewConfigResponse } from 'domain/payload/response/monitor/MonitorOverviewResp';
import { MonitorDailyPerformanceResp } from 'domain/payload/response/monitor/MonitorDailyPerformanceResp';
import { MonitorDailyPerformanceNewResp } from 'domain/payload/response/monitor/MonitorDailyPerformanceNewResp';

const MonitorService = () => {
  const dashboard = async (
    token: string,
    projectId: string | null,
    startDate?: string | null,
    endDate?: string | null
  ): Promise<BaseResponse<MonitorDashboardResp>> => {
    try {
      const response = await axiosInstance.get(`/v1/legacy/cms/monitor/dashboard`, {
        headers: {
          Authorization: `Bearer ${token}`
        },
        params: {
          projectId: projectId,
          startDate: startDate,
          endDate: endDate
        }
      });

      return response.data

    } catch (error: any) {
      throw error.response.data.message;
    }
  };

  const monitorByArea = async (
    token: string,
    projectId: string | null,
    areaId: string | null,
    groupName: string | null,
    startDate?: string | null,
    endDate?: string | null
  ): Promise<BaseResponse<MonitorByAreaResponse>> => {
    try {
      const response = await axiosInstance.get(`/v1/legacy/cms/monitor/area/dashboard`, {
        headers: {
          Authorization: `Bearer ${token}`
        },
        params: {
          projectId: projectId,
          areaId: areaId,
          startDate: startDate,
          endDate: endDate,
          groupName: groupName
        }
      });

      return response.data

    } catch (error: any) {
      throw error.response.data.message;
    }
  };

  const monitorConfig = async (
    token: string
  ): Promise<BaseResponse<DashboardConfigResp>> => {
    try {
      const response = await axiosInstance.get(`/v1/legacy/cms/monitor/config`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      return response.data

    } catch (error: any) {
      throw error.response.data.message;
    }
  };

  const qcMonitorConfig = async (
    token: string
  ): Promise<BaseResponse<DashboardConfigResp>> => {
    try {
      const response = await axiosInstance.get(`/v1/legacy/cms/qc-monitor/config`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      return response.data

    } catch (error: any) {
      throw error.response.data.message;
    }
  };

  const qcMonitorDashboard = async (
    token: string,
    projectId: string | null,
    startDate?: string | null,
    endDate?: string | null
  ): Promise<BaseResponse<any>> => {
    try {
      const response = await axiosInstance.get(`/v1/legacy/cms/qc-monitor/dashboard`, {
        headers: {
          Authorization: `Bearer ${token}`
        },
        params: {
          projectId: projectId === "all" ? null : projectId,
          startDate: startDate,
          endDate: endDate
        }
      });

      return response.data

    } catch (error: any) {
      throw error.response.data.message;
    }
  };

  const userGrowthDashboard = async (
    token: string,
    userType: 'moreAccess' | 'perfectStore',
    startDate?: string | null,
    endDate?: string | null
  ): Promise<BaseResponse<UserGrowthDashboardResp>> => {
    try {
      const response = await axiosInstance.get(`/v1/legacy/cms/monitor-user-growth/dashboard`, {
        headers: {
          Authorization: `Bearer ${token}`
        },
        params: {
          userType,
          startDate,
          endDate
        }
      });

      return response.data;
    } catch (error: any) {
      throw error.response?.data?.message || error.message;
    }
  };

  const getProjectsOverview = async (
    token: string,
    isRunning?: string,
    clientId?: string,
    cluster?: string,
    search?: string,
    page?: number,
    size?: number
  ): Promise<BaseResponse<MonitorOverviewProject[]>> => {
    try {
      const response = await axiosInstance.get(`/v1/legacy/cms/monitor/overview/projects`, {
        headers: {
          Authorization: `Bearer ${token}`
        },
        params: {
          isRunning: isRunning,
          clientId: clientId,
          cluster: cluster,
          search: search,
          page: page || 1,
          size: size || 10
        }
      });
      return response.data;
    } catch (error: any) {
      throw error.response?.data?.message || error.message;
    }
  };

  const getConfigOptions = async (
    token: string,
    isRunning?: string,
    clientId?: string,
    search?: string,
  ): Promise<BaseResponse<MonitorOverviewConfigResponse>> => {
    try {
      const response = await axiosInstance.get(`/v1/legacy/cms/monitor/overview/config`, {
        headers: {
          Authorization: `Bearer ${token}`
        },
        params: {
          isRunning: isRunning,
          clientId: clientId,
          search: search,
        }
      });
      return response.data;
    } catch (error: any) {
      throw error.response?.data?.message || error.message;
    }
  };

  const endProject = async (
    token: string,
    id: string
  ): Promise<BaseResponse<any>> => {
    try {
      const response = await axiosInstance.post(`/v1/legacy/cms/project/end`, { id }, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error: any) {
      throw error.response?.data?.message || error.message;
    }
  };

  const dailyPerformance = async (
    token: string,
    projectId: string | null,
    startDate?: string | null,
    endDate?: string | null
  ): Promise<BaseResponse<MonitorDailyPerformanceNewResp>> => {
    try {
      const response = await axiosInstance.get(`/v1/legacy/cms/monitor/daily-performance`, {
        headers: {
          Authorization: `Bearer ${token}`
        },
        params: {
          projectId: projectId,
          startDate: startDate,
          endDate: endDate
        }
      });

      return response.data

    } catch (error: any) {
      throw error.response.data.message;
    }
  };

  return { dashboard, monitorByArea, monitorConfig, qcMonitorConfig, qcMonitorDashboard, userGrowthDashboard, getProjectsOverview, getConfigOptions, endProject, dailyPerformance };
};

export default MonitorService;